import axios from "axios"
import { getTemplateConfigurationByVersion, getTemplates } from "shared/api/templateService"
import { getPermitDetail, getPermits, type PermitDetail, type PermitListItem } from "../../permit/api/permitService"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
const LOCAL_STORAGE_KEY = "permitfy_dynamic_report_templates_v1"
let reportApiUnavailable = false
const REPORT_IMAGE_UPLOAD_PERMIT_NUMBER =
  (process.env.REACT_APP_REPORT_IMAGE_UPLOAD_PERMIT_NUMBER ?? "").trim() || "REPORT_TEMPLATE"
const REPORT_IMAGE_UPLOAD_DOCUMENT_CODE =
  (process.env.REACT_APP_REPORT_IMAGE_UPLOAD_DOCUMENT_CODE ?? "").trim() || "REPORT_TEMPLATE_IMAGE"
const MIN_REPORT_FLOW_SECTION_HEIGHT = 20

const buildUrl = (path: string) => {
  if (API_BASE_URL) {
    return `${API_BASE_URL.replace(/\/$/, "")}${path}`
  }
  return path
}

const buildAttachmentFileUrl = (path?: string | null) => {
  if (!path) return ""
  if (/^https?:\/\//i.test(path)) return path
  const normalized = path.startsWith("/") ? path : `/${path}`
  if (!API_BASE_URL) return normalized
  return `${API_BASE_URL.replace(/\/$/, "")}${normalized}`
}

const isBrowser = typeof window !== "undefined"

const makeId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

const nowIso = () => new Date().toISOString()

const isEndpointMissing = (error: unknown) =>
  axios.isAxiosError(error) && [404, 405].includes(error.response?.status ?? 0)

const markApiUnavailable = () => {
  reportApiUnavailable = true
}

export type ReportTemplateStatusFilter = "all" | "active" | "inactive"

export interface ReportTemplateDto {
  reportTemplateId: string
  tenantId: string
  code: string
  name: string
  description?: string | null
  dataSourceType: string
  isActive: boolean
  version: number
  sourceTemplateVersionId?: string | null
  createdAt?: string | null
  updatedAt?: string | null
}

export interface ReportTemplateFieldDto {
  reportTemplateFieldId?: string
  fieldKey: string
  label: string
  dataType: "string" | "number" | "date" | "datetime" | "boolean" | "image"
  sourcePath?: string | null
  isFilterable: boolean
  isSortable: boolean
  isGroupable: boolean
  defaultVisible: boolean
  orderIndex: number
}

export interface ReportTemplateFilterDto {
  reportTemplateFilterId?: string
  fieldKey: string
  operator: string
  defaultValueJson?: string | null
  required: boolean
  orderIndex: number
}

export type ReportTemplateDatasourceType = "view"
export type ReportTemplateDatasourceCardinality = "record" | "table"

export interface ReportTemplateDatasourceBindingDto {
  id: string
  columnName: string
  contextKey: string
  operator?: "="
}

export interface ReportTemplateDatasourceFieldDto {
  fieldKey: string
  label: string
  dataType: "string" | "number" | "date" | "datetime" | "boolean" | "image"
}

export interface ReportTemplateDatasourceDto {
  id: string
  name: string
  sourceType: ReportTemplateDatasourceType
  objectName: string
  cardinality: ReportTemplateDatasourceCardinality
  rowLimit?: number
  bindings?: ReportTemplateDatasourceBindingDto[]
  fields?: ReportTemplateDatasourceFieldDto[]
}

export type ReportLayoutWidgetType = "text" | "field" | "image" | "table" | "line" | "rectangle"

export interface ReportLayoutWidgetDto {
  id: string
  type: ReportLayoutWidgetType
  x: number
  y: number
  width: number
  height: number
  zIndex: number
  props?: Record<string, unknown>
}

export interface ReportTemplateLayoutSectionDto {
  enabled: boolean
  height: number
  widgets: ReportLayoutWidgetDto[]
}

export type ReportFlowSectionBreakMode = "flow" | "newPage"

export interface ReportTemplateFlowSectionDto {
  sectionId: string
  name: string
  orderIndex: number
  breakMode: ReportFlowSectionBreakMode
  minHeight?: number
}

export type ReportTemplateFrameScope = "full" | "body"

export interface ReportTemplateFrameDto {
  enabled: boolean
  scope?: ReportTemplateFrameScope
  insetTop?: number
  insetRight?: number
  insetBottom?: number
  insetLeft?: number
  paddingTop?: number
  paddingRight?: number
  paddingBottom?: number
  paddingLeft?: number
  borderWidth?: number
  borderStyle?: "solid" | "dashed"
  borderColor?: string
  cornerRadius?: number
}

export interface ReportTemplateLayoutDto {
  pageSize: "A4" | "A3" | "Letter"
  orientation: "portrait" | "landscape"
  manualPageCount?: number
  datasources?: ReportTemplateDatasourceDto[]
  flowSections?: ReportTemplateFlowSectionDto[]
  frame?: ReportTemplateFrameDto
  widgets: ReportLayoutWidgetDto[]
  headerSection?: ReportTemplateLayoutSectionDto
  footerSection?: ReportTemplateLayoutSectionDto
}

export interface ReportTemplateDetailDto extends ReportTemplateDto {
  fields: ReportTemplateFieldDto[]
  filters: ReportTemplateFilterDto[]
  layoutJson?: ReportTemplateLayoutDto | null
}

export const SYSTEM_REPORT_FIELDS: ReportTemplateFieldDto[] = [
  {
    fieldKey: "permit_number",
    label: "Permit No.",
    dataType: "string",
    sourcePath: "permit.permitNumber",
    isFilterable: true,
    isSortable: true,
    isGroupable: false,
    defaultVisible: true,
    orderIndex: 10001,
  },
  {
    fieldKey: "permit_id",
    label: "Permit ID",
    dataType: "string",
    sourcePath: "permit.permitId",
    isFilterable: true,
    isSortable: true,
    isGroupable: false,
    defaultVisible: false,
    orderIndex: 10002,
  },
  {
    fieldKey: "title",
    label: "Title",
    dataType: "string",
    sourcePath: "permit.title",
    isFilterable: true,
    isSortable: true,
    isGroupable: false,
    defaultVisible: true,
    orderIndex: 10003,
  },
  {
    fieldKey: "status",
    label: "Status",
    dataType: "string",
    sourcePath: "permit.status",
    isFilterable: true,
    isSortable: true,
    isGroupable: false,
    defaultVisible: true,
    orderIndex: 10004,
  },
  {
    fieldKey: "requester_name",
    label: "Requester",
    dataType: "string",
    sourcePath: "permit.requesterName",
    isFilterable: true,
    isSortable: true,
    isGroupable: false,
    defaultVisible: true,
    orderIndex: 10005,
  },
  {
    fieldKey: "planned_start",
    label: "Planned Start",
    dataType: "datetime",
    sourcePath: "permit.plannedStart",
    isFilterable: true,
    isSortable: true,
    isGroupable: false,
    defaultVisible: true,
    orderIndex: 10006,
  },
  {
    fieldKey: "planned_end",
    label: "Planned End",
    dataType: "datetime",
    sourcePath: "permit.plannedEnd",
    isFilterable: true,
    isSortable: true,
    isGroupable: false,
    defaultVisible: true,
    orderIndex: 10007,
  },
]

export const mergeSystemReportFields = (fields: ReportTemplateFieldDto[]): ReportTemplateFieldDto[] => {
  const next = [...fields]
  const seen = new Set(
    fields
      .map((field) => String(field.fieldKey || "").trim().toLowerCase())
      .filter(Boolean),
  )

  SYSTEM_REPORT_FIELDS.forEach((field) => {
    const key = String(field.fieldKey || "").trim().toLowerCase()
    if (!key || seen.has(key)) return
    next.push({ ...field })
    seen.add(key)
  })

  return next
}

export interface ReportTemplateUpsertRequest {
  tenantId: string
  code: string
  name: string
  description?: string | null
  dataSourceType: string
  isActive: boolean
}

export interface PagedResultDto<T> {
  items: T[]
  pageNumber: number
  pageSize: number
  totalItems: number
  totalPages: number
}

interface SearchReportTemplatesParams {
  tenantId: string
  search?: string
  status?: ReportTemplateStatusFilter
  page?: number
  pageSize?: number
}

export interface UpdateReportTemplateDefinitionRequest {
  tenantId: string
  fields: ReportTemplateFieldDto[]
  filters: ReportTemplateFilterDto[]
  layoutJson?: ReportTemplateLayoutDto | null
}

export interface ReportRunFilterInput {
  fieldKey: string
  operator: string
  value?: string | number | boolean | null | Array<string | number | boolean>
  valueTo?: string | number | boolean | null
}

export interface RunReportRequest {
  tenantId: string
  templateId: string
  columns?: string[]
  filters?: ReportRunFilterInput[]
  sort?: Array<{ fieldKey: string; direction: "asc" | "desc" }>
  groupBy?: string[]
  pageNumber?: number
  pageSize?: number
}

export interface RunReportResult {
  rows: Array<Record<string, unknown>>
  pageNumber: number
  pageSize: number
  totalItems: number
  totalPages: number
  elapsedMs?: number
  source: "api" | "fallback"
}

export interface ExportReportPdfRequest {
  tenantId: string
  templateId: string
  columns?: string[]
  filters?: ReportRunFilterInput[]
  sort?: Array<{ fieldKey: string; direction: "asc" | "desc" }>
  groupBy?: string[]
}

export interface UploadReportTemplateImageRequest {
  tenantId: string
  file: File
  permitNumber?: string
  documentCode?: string
}

export interface UploadReportTemplateImageResponse {
  fileName: string
  storedFileName: string
  storagePath: string
  contentType: string
  size: number
  fileUrl: string
}

export interface ReportDatasourceViewOptionDto {
  schemaName: string
  viewName: string
  objectName: string
}

export interface PreviewReportDatasourceRequest {
  tenantId: string
  sourceType: ReportTemplateDatasourceType
  objectName: string
  cardinality: ReportTemplateDatasourceCardinality
  bindings?: ReportTemplateDatasourceBindingDto[]
  context?: Record<string, string | number | boolean | null>
  rowLimit?: number
}

export interface PreviewReportDatasourceResult {
  cardinality: ReportTemplateDatasourceCardinality
  rowCount: number
  columns: ReportTemplateDatasourceFieldDto[]
  record?: Record<string, unknown> | null
  rows: Array<Record<string, unknown>>
}

type LocalStore = {
  templates: ReportTemplateDto[]
  details: Record<string, ReportTemplateDetailDto>
}

const emptyStore = (): LocalStore => ({
  templates: [],
  details: {},
})

const readStore = (): LocalStore => {
  if (!isBrowser) return emptyStore()
  const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY)
  if (!raw) return emptyStore()

  try {
    const parsed = JSON.parse(raw) as Partial<LocalStore>
    return {
      templates: Array.isArray(parsed.templates) ? parsed.templates : [],
      details: parsed.details && typeof parsed.details === "object" ? parsed.details as Record<string, ReportTemplateDetailDto> : {},
    }
  } catch {
    return emptyStore()
  }
}

const writeStore = (store: LocalStore) => {
  if (!isBrowser) return
  window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(store))
}

const toBoolean = (value: unknown, defaultValue = false) => {
  if (typeof value === "boolean") return value
  if (typeof value === "number") return value !== 0
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase()
    if (["true", "1", "yes", "y"].includes(normalized)) return true
    if (["false", "0", "no", "n"].includes(normalized)) return false
  }
  return defaultValue
}

const normalizePagedResponse = <T>(
  payload: PagedResultDto<T> | T[] | null | undefined,
  fallbackPage: number,
  fallbackPageSize: number,
): PagedResultDto<T> => {
  if (Array.isArray(payload)) {
    return {
      items: payload,
      pageNumber: fallbackPage,
      pageSize: fallbackPageSize,
      totalItems: payload.length,
      totalPages: 1,
    }
  }

  if (!payload) {
    return {
      items: [],
      pageNumber: fallbackPage,
      pageSize: fallbackPageSize,
      totalItems: 0,
      totalPages: 0,
    }
  }

  return {
    items: payload.items ?? [],
    pageNumber: payload.pageNumber ?? fallbackPage,
    pageSize: payload.pageSize ?? fallbackPageSize,
    totalItems: payload.totalItems ?? payload.items?.length ?? 0,
    totalPages:
      payload.totalPages ??
      Math.ceil((payload.totalItems ?? payload.items?.length ?? 0) / (payload.pageSize ?? fallbackPageSize)),
  }
}

const normalizeField = (raw: any, index: number): ReportTemplateFieldDto => ({
  reportTemplateFieldId:
    raw?.reportTemplateFieldId ??
    raw?.fieldId ??
    raw?.id ??
    undefined,
  fieldKey: String(raw?.fieldKey ?? raw?.key ?? "").trim(),
  label: String(raw?.label ?? raw?.name ?? "").trim(),
  dataType:
    String(raw?.dataType ?? raw?.type ?? "string").toLowerCase() as ReportTemplateFieldDto["dataType"],
  sourcePath: raw?.sourcePath ?? raw?.source ?? null,
  isFilterable: toBoolean(raw?.isFilterable, true),
  isSortable: toBoolean(raw?.isSortable, true),
  isGroupable: toBoolean(raw?.isGroupable, false),
  defaultVisible: toBoolean(raw?.defaultVisible, true),
  orderIndex: Number(raw?.orderIndex ?? index + 1),
})

const normalizeFilter = (raw: any, index: number): ReportTemplateFilterDto => ({
  reportTemplateFilterId:
    raw?.reportTemplateFilterId ??
    raw?.filterId ??
    raw?.id ??
    undefined,
  fieldKey: String(raw?.fieldKey ?? raw?.key ?? "").trim(),
  operator: String(raw?.operator ?? "=").trim(),
  defaultValueJson:
    typeof raw?.defaultValueJson === "string"
      ? raw.defaultValueJson
      : raw?.defaultValueJson != null
        ? JSON.stringify(raw.defaultValueJson)
        : null,
  required: toBoolean(raw?.required, false),
  orderIndex: Number(raw?.orderIndex ?? index + 1),
})

const clampNumber = (value: unknown, fallback: number) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return parsed
}

const normalizeDatasourceField = (raw: any): ReportTemplateDatasourceFieldDto => ({
  fieldKey: String(raw?.fieldKey ?? raw?.key ?? raw?.name ?? "").trim(),
  label: String(raw?.label ?? raw?.name ?? raw?.fieldKey ?? raw?.key ?? "").trim(),
  dataType:
    String(raw?.dataType ?? raw?.type ?? "string").toLowerCase() as ReportTemplateDatasourceFieldDto["dataType"],
})

const normalizeDatasourceBinding = (raw: any, index: number): ReportTemplateDatasourceBindingDto => ({
  id: String(raw?.id ?? `binding-${index + 1}`).trim(),
  columnName: String(raw?.columnName ?? raw?.column ?? "").trim(),
  contextKey: String(raw?.contextKey ?? raw?.sourceKey ?? "").trim(),
  operator: "=",
})

const normalizeDatasource = (raw: any, index: number): ReportTemplateDatasourceDto | null => {
  if (!raw || typeof raw !== "object") return null

  const sourceType: ReportTemplateDatasourceType = "view"
  const cardinalityRaw = String(raw?.cardinality ?? "record").trim().toLowerCase()

  return {
    id: String(raw?.id ?? `datasource-${index + 1}`).trim() || `datasource-${index + 1}`,
    name: String(raw?.name ?? `Datasource ${index + 1}`).trim() || `Datasource ${index + 1}`,
    sourceType,
    objectName: String(raw?.objectName ?? raw?.viewName ?? "").trim(),
    cardinality: cardinalityRaw === "table" ? "table" : "record",
    rowLimit: Math.max(1, Math.min(200, Math.round(clampNumber(raw?.rowLimit, 20)))),
    bindings: Array.isArray(raw?.bindings)
      ? raw.bindings
          .map(normalizeDatasourceBinding)
          .filter((item: ReportTemplateDatasourceBindingDto) => item.columnName && item.contextKey)
      : [],
    fields: Array.isArray(raw?.fields)
      ? raw.fields
          .map(normalizeDatasourceField)
          .filter((item: ReportTemplateDatasourceFieldDto) => item.fieldKey)
      : [],
  }
}

const normalizeLayout = (rawLayout: any): ReportTemplateLayoutDto | null => {
  if (!rawLayout) return null

  let parsed = rawLayout
  if (typeof rawLayout === "string") {
    try {
      parsed = JSON.parse(rawLayout)
    } catch {
      return null
    }
  }
  if (!parsed || typeof parsed !== "object") return null

  const pageSizeRaw = String(parsed.pageSize || "A4").toUpperCase()
  const orientationRaw = String(parsed.orientation || "portrait").toLowerCase()
  const pageSize: ReportTemplateLayoutDto["pageSize"] =
    pageSizeRaw === "A3" ? "A3" : pageSizeRaw === "LETTER" ? "Letter" : "A4"
  const orientation: ReportTemplateLayoutDto["orientation"] =
    orientationRaw === "landscape" ? "landscape" : "portrait"

  const normalizeWidgets = (raw: unknown, zIndexSeed = 0): ReportLayoutWidgetDto[] => {
    const rawWidgets = Array.isArray(raw) ? raw : []
    return rawWidgets
      .map((item: any, index: number) => {
        const typeRaw = String(item?.type || "").toLowerCase()
        const type: ReportLayoutWidgetType =
          typeRaw === "field" ||
          typeRaw === "image" ||
          typeRaw === "table" ||
          typeRaw === "line" ||
          typeRaw === "rectangle"
            ? typeRaw
            : "text"

        const fallbackId = `widget-${zIndexSeed + index + 1}`
        return {
          id: String(item?.id || fallbackId),
          type,
          x: clampNumber(item?.x, 50),
          y: clampNumber(item?.y, 50 + index * 24),
          width: clampNumber(item?.width, 180),
          height: clampNumber(item?.height, type === "table" ? 140 : type === "rectangle" ? 120 : 48),
          zIndex: clampNumber(item?.zIndex, zIndexSeed + index + 1),
          props: item?.props && typeof item.props === "object" ? item.props : {},
        }
      })
      .sort((a: ReportLayoutWidgetDto, b: ReportLayoutWidgetDto) => a.zIndex - b.zIndex)
  }

  const widgets: ReportLayoutWidgetDto[] = normalizeWidgets(parsed.widgets, 0)
  const normalizeSection = (rawSection: unknown, defaults: { enabled: boolean; height: number }) => {
    if (!rawSection || typeof rawSection !== "object") {
      return {
        enabled: defaults.enabled,
        height: defaults.height,
        widgets: [] as ReportLayoutWidgetDto[],
      }
    }
    const section = rawSection as Record<string, unknown>
    return {
      enabled: toBoolean(section.enabled, defaults.enabled),
      height: clampNumber(section.height, defaults.height),
      widgets: normalizeWidgets(section.widgets, 1000),
    }
  }

  const normalizeFlowSections = (rawSections: unknown): ReportTemplateFlowSectionDto[] => {
    const list = Array.isArray(rawSections) ? rawSections : []
    const normalized = list
      .map((item: any, index): ReportTemplateFlowSectionDto | null => {
        const sectionId = String(item?.sectionId ?? item?.id ?? "").trim()
        if (!sectionId) return null
        const name = String(item?.name ?? `Section ${index + 1}`).trim() || `Section ${index + 1}`
        const orderIndex = Math.max(1, Math.floor(clampNumber(item?.orderIndex, index + 1)))
        const breakModeRaw = String(item?.breakMode ?? "flow").toLowerCase()
        const breakMode: ReportFlowSectionBreakMode = breakModeRaw === "newpage" ? "newPage" : "flow"
        const minHeight = Math.max(MIN_REPORT_FLOW_SECTION_HEIGHT, Math.floor(clampNumber(item?.minHeight, 140)))
        return { sectionId, name, orderIndex, breakMode, minHeight }
      })
      .filter((item): item is ReportTemplateFlowSectionDto => item !== null)
      .sort((a, b) => a.orderIndex - b.orderIndex)

    if (normalized.length > 0) return normalized
    return [
      {
        sectionId: "section-default",
        name: "Default Section",
        orderIndex: 1,
        breakMode: "flow",
        minHeight: 140,
      },
    ]
  }

  const normalizeFrame = (rawFrame: unknown): ReportTemplateFrameDto => {
    const frame = rawFrame && typeof rawFrame === "object" ? (rawFrame as Record<string, unknown>) : {}
    const borderStyleRaw = String(frame.borderStyle ?? "solid").trim().toLowerCase()
    const scopeRaw = String(frame.scope ?? "full").trim().toLowerCase()

    return {
      enabled: toBoolean(frame.enabled, false),
      scope: scopeRaw === "body" ? "body" : "full",
      insetTop: Math.max(0, Math.floor(clampNumber(frame.insetTop, 0))),
      insetRight: Math.max(0, Math.floor(clampNumber(frame.insetRight, 0))),
      insetBottom: Math.max(0, Math.floor(clampNumber(frame.insetBottom, 0))),
      insetLeft: Math.max(0, Math.floor(clampNumber(frame.insetLeft, 0))),
      paddingTop: Math.max(0, Math.floor(clampNumber(frame.paddingTop, 0))),
      paddingRight: Math.max(0, Math.floor(clampNumber(frame.paddingRight, 0))),
      paddingBottom: Math.max(0, Math.floor(clampNumber(frame.paddingBottom, 0))),
      paddingLeft: Math.max(0, Math.floor(clampNumber(frame.paddingLeft, 0))),
      borderWidth: Math.max(1, Math.floor(clampNumber(frame.borderWidth, 1))),
      borderStyle: borderStyleRaw === "dashed" ? "dashed" : "solid",
      borderColor: String(frame.borderColor ?? "#334155"),
      cornerRadius: Math.max(0, Math.floor(clampNumber(frame.cornerRadius, 0))),
    }
  }

  return {
    pageSize,
    orientation,
    manualPageCount: Math.max(1, Math.floor(clampNumber(parsed.manualPageCount, 1))),
    datasources: Array.isArray(parsed.datasources)
      ? parsed.datasources
          .map(normalizeDatasource)
          .filter((item: ReportTemplateDatasourceDto | null): item is ReportTemplateDatasourceDto => item !== null)
      : [],
    flowSections: normalizeFlowSections(parsed.flowSections),
    frame: normalizeFrame(parsed.frame),
    widgets,
    headerSection: normalizeSection(parsed.headerSection, { enabled: false, height: 120 }),
    footerSection: normalizeSection(parsed.footerSection, { enabled: false, height: 80 }),
  }
}

const normalizeDetail = (payload: any): ReportTemplateDetailDto => {
  const rawFields = payload?.fields ?? payload?.templateFields ?? payload?.reportTemplateFields ?? []
  const rawFilters = payload?.filters ?? payload?.templateFilters ?? payload?.reportTemplateFilters ?? []
  const rawLayout = payload?.layoutJson ?? payload?.layout_json ?? payload?.layout ?? null

  return {
    reportTemplateId: payload?.reportTemplateId ?? payload?.id ?? "",
    tenantId: payload?.tenantId ?? "",
    code: payload?.code ?? "",
    name: payload?.name ?? "",
    description: payload?.description ?? null,
    dataSourceType: payload?.dataSourceType ?? "PERMIT",
    isActive: toBoolean(payload?.isActive, true),
    version: Number(payload?.version ?? 1),
    sourceTemplateVersionId: payload?.sourceTemplateVersionId ?? null,
    createdAt: payload?.createdAt ?? null,
    updatedAt: payload?.updatedAt ?? null,
    fields: Array.isArray(rawFields) ? rawFields.map(normalizeField) : [],
    filters: Array.isArray(rawFilters) ? rawFilters.map(normalizeFilter) : [],
    layoutJson: normalizeLayout(rawLayout),
  }
}

const toReportDataType = (fieldType?: string): ReportTemplateFieldDto["dataType"] => {
  const value = String(fieldType || "").toLowerCase()
  if (["number", "int", "decimal"].includes(value)) return "number"
  if (value === "date") return "date"
  if (value === "time" || value === "datetime") return "datetime"
  if (value === "checkbox" || value === "boolean") return "boolean"
  if (value === "attachment" || value === "image") return "image"
  return "string"
}

const normalizeFieldKey = (value?: string | null) => String(value || "").trim().toLowerCase()

const getDynamicFieldCode = (fieldKey?: string | null, sourcePath?: string | null) => {
  const key = String(fieldKey || "").trim()
  const source = String(sourcePath || "").trim()

  if (source.toLowerCase().startsWith("dynamic.")) {
    return source.slice(8).trim().toUpperCase()
  }
  if (key.toLowerCase().startsWith("dynamic.")) {
    return key.slice(8).trim().toUpperCase()
  }
  return key.toUpperCase()
}

const extractDynamicFieldValue = (detail: PermitDetail | null, fieldKey?: string | null, sourcePath?: string | null): unknown => {
  if (!detail) return null
  const dynamicCode = getDynamicFieldCode(fieldKey, sourcePath)
  if (!dynamicCode) return null

  const found = (detail.fieldValues ?? []).find((item) => String(item.fieldCode || "").trim().toUpperCase() === dynamicCode)
  if (!found) return null

  if (found.displayValue != null && String(found.displayValue).trim() !== "") return found.displayValue
  if (found.valueText != null && String(found.valueText).trim() !== "") return found.valueText
  if (found.valueNumber != null) return found.valueNumber
  if (found.valueBoolean != null) return found.valueBoolean
  if (found.valueDate != null && String(found.valueDate).trim() !== "") return found.valueDate
  if (found.valueTimestamp != null && String(found.valueTimestamp).trim() !== "") return found.valueTimestamp

  return null
}

const toStringArray = (value: unknown): string[] => {
  if (value == null) return []
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean)
  }
  const raw = String(value).trim()
  if (!raw) return []
  if (raw.includes(",")) {
    return raw
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
  }
  return [raw]
}

const applyFilterToPermitQuery = (
  query: {
    searchTerm?: string
    statuses?: string[]
    permitTypes?: string[]
    plannedStartFrom?: string
    plannedStartTo?: string
  },
  filter: ReportRunFilterInput,
) => {
  const key = normalizeFieldKey(filter.fieldKey)
  const operator = String(filter.operator || "=").trim().toLowerCase()
  const values = toStringArray(filter.value)
  const firstValue = values[0]

  if (!firstValue && operator !== "between" && values.length === 0) return

  if (["permit_number", "title", "work_description", "requester_name", "search"].includes(key)) {
    if (operator === "contains" || operator === "=") {
      query.searchTerm = firstValue || query.searchTerm
    }
    return
  }

  if (["status", "permit_status"].includes(key)) {
    query.statuses = values.length > 0 ? values : query.statuses
    return
  }

  if (["permit_type", "permit_type_code", "permittype"].includes(key)) {
    query.permitTypes = values.length > 0 ? values : query.permitTypes
    return
  }

  if (["planned_start", "work_date_start", "start_date", "planned_date"].includes(key)) {
    if (operator === "between") {
      query.plannedStartFrom = firstValue || query.plannedStartFrom
      query.plannedStartTo = filter.valueTo ? String(filter.valueTo) : query.plannedStartTo
      return
    }
    if (operator === ">=" || operator === ">" || operator === "=") {
      query.plannedStartFrom = firstValue || query.plannedStartFrom
      return
    }
    if (operator === "<=" || operator === "<") {
      query.plannedStartTo = firstValue || query.plannedStartTo
      return
    }
  }
}

const getMappedPermitValue = (permit: PermitListItem, key: string): unknown => {
  switch (key) {
    case "permit_id":
    case "permitid":
      return permit.permitId
    case "permit_number":
    case "permitnumber":
      return permit.permitNumber
    case "title":
      return permit.title
    case "status":
      return permit.status
    case "status_name":
    case "statusname":
      return permit.statusName
    case "permit_type":
    case "permittype":
    case "permit_type_code":
      return permit.permitType
    case "work_description":
    case "workdescription":
      return permit.workDescription
    case "location":
      return permit.location
    case "location_name":
      return permit.locationName
    case "unit":
      return permit.unit
    case "unit_name":
      return permit.unitName
    case "requester_name":
      return permit.requesterName
    case "requester_company":
      return permit.requesterCompany
    case "approved_by":
      return permit.approvedBy
    case "risk_level":
      return permit.riskLevel
    case "planned_start":
      return permit.plannedStart
    case "planned_end":
      return permit.plannedEnd
    case "created_at":
      return permit.createdAt
    default:
      return null
  }
}

const normalizeForCompare = (value: unknown): number | string => {
  if (value == null) return ""
  if (typeof value === "number") return Number.isNaN(value) ? 0 : value
  if (typeof value === "boolean") return value ? 1 : 0

  const text = String(value).trim()
  if (!text) return ""

  const maybeNumber = Number(text)
  if (!Number.isNaN(maybeNumber) && text !== "") return maybeNumber

  const maybeTime = Date.parse(text)
  if (!Number.isNaN(maybeTime)) return maybeTime

  return text.toLowerCase()
}

const sortRows = (
  rows: Array<Record<string, unknown>>,
  sortInputs?: Array<{ fieldKey: string; direction: "asc" | "desc" }>,
) => {
  if (!sortInputs || sortInputs.length === 0) return rows

  const activeSorts = sortInputs
    .filter((item) => String(item?.fieldKey || "").trim())
    .map((item) => ({
      fieldKey: String(item.fieldKey).trim(),
      direction: item.direction === "desc" ? "desc" : "asc",
    }))

  if (activeSorts.length === 0) return rows

  return [...rows].sort((left, right) => {
    for (const sort of activeSorts) {
      const leftRaw = normalizeForCompare(left[sort.fieldKey])
      const rightRaw = normalizeForCompare(right[sort.fieldKey])

      if (leftRaw === rightRaw) continue
      const result = leftRaw > rightRaw ? 1 : -1
      return sort.direction === "desc" ? -result : result
    }
    return 0
  })
}

const runFromPermitFallback = async (request: RunReportRequest): Promise<RunReportResult> => {
  const started = Date.now()
  const pageNumber = Math.max(1, request.pageNumber ?? 1)
  const pageSize = Math.max(1, request.pageSize ?? 20)

  const detail = await getReportTemplate(request.templateId, request.tenantId)
  const fields = detail.fields ?? []
  const fieldMap = new Map(fields.map((item) => [item.fieldKey, item]))
  const selectedFieldKeys = (request.columns && request.columns.length > 0
    ? request.columns
    : fields.filter((item) => item.defaultVisible).map((item) => item.fieldKey)
  ).filter(Boolean)

  const query: {
    searchTerm?: string
    statuses?: string[]
    permitTypes?: string[]
    plannedStartFrom?: string
    plannedStartTo?: string
  } = {}

  ;(request.filters ?? []).forEach((filter) => applyFilterToPermitQuery(query, filter))

  const permits = await getPermits({
    tenantId: request.tenantId,
    searchTerm: query.searchTerm,
    statuses: query.statuses,
    permitTypes: query.permitTypes,
    plannedStartFrom: query.plannedStartFrom,
    plannedStartTo: query.plannedStartTo,
    pageNumber,
    pageSize,
  })

  const needsDynamicDetails = selectedFieldKeys.some((fieldKey) => {
    const field = fieldMap.get(fieldKey)
    const key = normalizeFieldKey(fieldKey)
    const source = normalizeFieldKey(field?.sourcePath)
    if (source.startsWith("dynamic.")) return true
    return ![
      "permit_id",
      "permitid",
      "permit_number",
      "permitnumber",
      "title",
      "status",
      "status_name",
      "statusname",
      "permit_type",
      "permittype",
      "permit_type_code",
      "work_description",
      "workdescription",
      "location",
      "location_name",
      "unit",
      "unit_name",
      "requester_name",
      "requester_company",
      "approved_by",
      "risk_level",
      "planned_start",
      "planned_end",
      "created_at",
    ].includes(key)
  })

  const detailMap = new Map<string, PermitDetail | null>()
  if (needsDynamicDetails) {
    await Promise.all(
      (permits.items ?? []).map(async (item) => {
        try {
          const permitDetail = await getPermitDetail(request.tenantId, item.permitId)
          detailMap.set(item.permitId, permitDetail)
        } catch {
          detailMap.set(item.permitId, null)
        }
      }),
    )
  }

  const rows = (permits.items ?? []).map((permit) => {
    const row: Record<string, unknown> = {}
    selectedFieldKeys.forEach((fieldKey) => {
      const field = fieldMap.get(fieldKey)
      const normalizedKey = normalizeFieldKey(fieldKey)
      const mapped = getMappedPermitValue(permit, normalizedKey)

      if (mapped !== null && mapped !== undefined && mapped !== "") {
        row[fieldKey] = mapped
        return
      }

      row[fieldKey] = extractDynamicFieldValue(detailMap.get(permit.permitId) ?? null, fieldKey, field?.sourcePath)
    })

    row.__permitId = permit.permitId
    row.__permitNumber = permit.permitNumber
    return row
  })

  const groupSorts = (request.groupBy ?? [])
    .map((fieldKey) => String(fieldKey || "").trim())
    .filter(Boolean)
    .map((fieldKey) => ({ fieldKey, direction: "asc" as const }))

  const userSorts = (request.sort ?? [])
    .map((item) => ({
      fieldKey: String(item.fieldKey || "").trim(),
      direction: (item.direction === "desc" ? "desc" : "asc") as "asc" | "desc",
    }))
    .filter((item) => item.fieldKey)

  const mergedSorts = [
    ...groupSorts,
    ...userSorts.filter((sort) => !groupSorts.some((groupSort) => groupSort.fieldKey === sort.fieldKey)),
  ]

  const sortedRows = sortRows(rows, mergedSorts)

  return {
    rows: sortedRows,
    pageNumber: permits.pageNumber ?? pageNumber,
    pageSize: permits.pageSize ?? pageSize,
    totalItems: permits.totalItems ?? sortedRows.length,
    totalPages: permits.totalPages ?? Math.max(1, Math.ceil((permits.totalItems ?? sortedRows.length) / pageSize)),
    elapsedMs: Date.now() - started,
    source: "fallback",
  }
}

const buildFieldsFromTemplateVersion = async (tenantId: string, templateVersionId: string): Promise<ReportTemplateFieldDto[]> => {
  try {
    const configuration = await getTemplateConfigurationByVersion(tenantId, templateVersionId)
    if (!configuration) return []

    const mapped = (configuration.sections ?? []).flatMap((section) => {
      return (section.fields ?? []).map((field) => ({
        reportTemplateFieldId: field.fieldId,
        fieldKey: field.code,
        label: field.label,
        dataType: toReportDataType(field.fieldType),
        sourcePath: `dynamic.${field.code}`,
        isFilterable: true,
        isSortable: true,
        isGroupable: false,
        defaultVisible: true,
        orderIndex: field.orderIndex || 1,
      }))
    })

    const uniqueByKey: Record<string, ReportTemplateFieldDto> = {}
    mapped.forEach((item) => {
      if (!item.fieldKey) return
      if (!uniqueByKey[item.fieldKey]) {
        uniqueByKey[item.fieldKey] = item
      }
    })
    return Object.values(uniqueByKey).sort((a, b) => a.orderIndex - b.orderIndex)
  } catch {
    return []
  }
}

const ensureLocalSeed = async (tenantId: string): Promise<LocalStore> => {
  const store = readStore()
  let changed = false

  try {
    const templateSummaries = await getTemplates(tenantId)
    templateSummaries.forEach((item) => {
      const seedId = `seed-${item.templateVersionId}`
      const exists = store.templates.some((x) => x.reportTemplateId === seedId)
      if (exists) return

      store.templates.push({
        reportTemplateId: seedId,
        tenantId,
        code: item.permitCode,
        name: `${item.templateName} Report`,
        description: item.description ?? "Seeded from Dynamic Form Template",
        dataSourceType: "PERMIT",
        isActive: true,
        version: 1,
        sourceTemplateVersionId: item.templateVersionId,
        createdAt: item.createdAt ?? nowIso(),
        updatedAt: item.createdAt ?? nowIso(),
      })
      changed = true
    })
  } catch {
    // Ignore seeding failures. Local mode will still work for manually created templates.
  }

  if (changed) {
    writeStore(store)
  }
  return store
}

const toPagedResult = (
  items: ReportTemplateDto[],
  page: number,
  pageSize: number,
): PagedResultDto<ReportTemplateDto> => {
  const safePage = Math.max(1, page)
  const safePageSize = Math.max(1, pageSize)
  const totalItems = items.length
  const totalPages = Math.max(1, Math.ceil(totalItems / safePageSize))
  const start = (safePage - 1) * safePageSize
  return {
    items: items.slice(start, start + safePageSize),
    pageNumber: safePage,
    pageSize: safePageSize,
    totalItems,
    totalPages,
  }
}

const searchInLocalStore = async ({
  tenantId,
  search,
  status = "all",
  page = 1,
  pageSize = 20,
}: SearchReportTemplatesParams): Promise<PagedResultDto<ReportTemplateDto>> => {
  const store = await ensureLocalSeed(tenantId)
  const keyword = (search || "").trim().toLowerCase()

  const filtered = store.templates
    .filter((item) => item.tenantId === tenantId)
    .filter((item) => {
      if (status === "active") return item.isActive
      if (status === "inactive") return !item.isActive
      return true
    })
    .filter((item) => {
      if (!keyword) return true
      return (
        item.code.toLowerCase().includes(keyword) ||
        item.name.toLowerCase().includes(keyword) ||
        String(item.description || "").toLowerCase().includes(keyword)
      )
    })
    .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")))

  return toPagedResult(filtered, page, pageSize)
}

const createInLocalStore = async (payload: ReportTemplateUpsertRequest): Promise<ReportTemplateDto> => {
  const store = await ensureLocalSeed(payload.tenantId)
  const now = nowIso()
  const item: ReportTemplateDto = {
    reportTemplateId: makeId(),
    tenantId: payload.tenantId,
    code: payload.code.trim().toUpperCase(),
    name: payload.name.trim(),
    description: payload.description ?? null,
    dataSourceType: payload.dataSourceType.trim().toUpperCase(),
    isActive: payload.isActive,
    version: 1,
    createdAt: now,
    updatedAt: now,
    sourceTemplateVersionId: null,
  }

  store.templates.push(item)
  store.details[item.reportTemplateId] = {
    ...item,
    fields: [],
    filters: [],
    layoutJson: null,
  }
  writeStore(store)
  return item
}

const updateInLocalStore = async (
  reportTemplateId: string,
  payload: ReportTemplateUpsertRequest,
): Promise<ReportTemplateDto> => {
  const store = await ensureLocalSeed(payload.tenantId)
  const index = store.templates.findIndex((item) => item.reportTemplateId === reportTemplateId)
  if (index < 0) {
    throw new Error("Report template not found")
  }

  const existing = store.templates[index]
  const updated: ReportTemplateDto = {
    ...existing,
    tenantId: payload.tenantId,
    code: payload.code.trim().toUpperCase(),
    name: payload.name.trim(),
    description: payload.description ?? null,
    dataSourceType: payload.dataSourceType.trim().toUpperCase(),
    isActive: payload.isActive,
    updatedAt: nowIso(),
  }
  store.templates[index] = updated

  if (store.details[reportTemplateId]) {
    store.details[reportTemplateId] = {
      ...store.details[reportTemplateId],
      ...updated,
    }
  }

  writeStore(store)
  return updated
}

const activateInLocalStore = async (reportTemplateId: string, tenantId: string, isActive: boolean): Promise<void> => {
  const store = await ensureLocalSeed(tenantId)
  const index = store.templates.findIndex((item) => item.reportTemplateId === reportTemplateId && item.tenantId === tenantId)
  if (index < 0) return
  store.templates[index] = {
    ...store.templates[index],
    isActive,
    updatedAt: nowIso(),
  }
  if (store.details[reportTemplateId]) {
    store.details[reportTemplateId].isActive = isActive
    store.details[reportTemplateId].updatedAt = nowIso()
  }
  writeStore(store)
}

const getDetailFromLocalStore = async (reportTemplateId: string, tenantId?: string): Promise<ReportTemplateDetailDto> => {
  const requestedTenantId = tenantId || ""
  const store = await ensureLocalSeed(requestedTenantId)

  const cached = store.details[reportTemplateId]
  if (cached) {
    return cached
  }

  const template = store.templates.find((item) => item.reportTemplateId === reportTemplateId)
  if (!template) {
    throw new Error("Report template not found")
  }

  let fields: ReportTemplateFieldDto[] = []
  if (template.sourceTemplateVersionId) {
    fields = await buildFieldsFromTemplateVersion(template.tenantId, template.sourceTemplateVersionId)
  }

  const detail: ReportTemplateDetailDto = {
    ...template,
    fields,
    filters: [],
    layoutJson: null,
  }
  store.details[reportTemplateId] = detail
  writeStore(store)
  return detail
}

const cloneInLocalStore = async (reportTemplateId: string, tenantId: string): Promise<ReportTemplateDto> => {
  const store = await ensureLocalSeed(tenantId)
  const source = store.templates.find((item) => item.reportTemplateId === reportTemplateId && item.tenantId === tenantId)
  if (!source) {
    throw new Error("Report template not found")
  }

  const now = nowIso()
  const suffix = Date.now().toString().slice(-4)
  const clone: ReportTemplateDto = {
    ...source,
    reportTemplateId: makeId(),
    code: `${source.code}_COPY_${suffix}`.slice(0, 60),
    name: `${source.name} (Copy)`,
    isActive: false,
    createdAt: now,
    updatedAt: now,
  }

  store.templates.push(clone)

  const detail = store.details[source.reportTemplateId]
  if (detail) {
    store.details[clone.reportTemplateId] = {
      ...detail,
      ...clone,
      fields: detail.fields.map((item) => ({ ...item })),
      filters: detail.filters.map((item) => ({ ...item })),
      layoutJson: detail.layoutJson
        ? {
            ...detail.layoutJson,
            datasources: (detail.layoutJson.datasources ?? []).map((datasource) => ({
              ...datasource,
              bindings: (datasource.bindings ?? []).map((binding) => ({ ...binding })),
              fields: (datasource.fields ?? []).map((field) => ({ ...field })),
            })),
            widgets: (detail.layoutJson.widgets ?? []).map((widget) => ({
              ...widget,
              props: widget.props && typeof widget.props === "object" ? { ...widget.props } : {},
            })),
            headerSection: detail.layoutJson.headerSection
              ? {
                  ...detail.layoutJson.headerSection,
                  widgets: (detail.layoutJson.headerSection.widgets ?? []).map((widget) => ({
                    ...widget,
                    props: widget.props && typeof widget.props === "object" ? { ...widget.props } : {},
                  })),
                }
              : undefined,
            footerSection: detail.layoutJson.footerSection
              ? {
                  ...detail.layoutJson.footerSection,
                  widgets: (detail.layoutJson.footerSection.widgets ?? []).map((widget) => ({
                    ...widget,
                    props: widget.props && typeof widget.props === "object" ? { ...widget.props } : {},
                  })),
                }
              : undefined,
          }
        : null,
    }
  }

  writeStore(store)
  return clone
}

const updateDefinitionInLocalStore = async (
  reportTemplateId: string,
  payload: UpdateReportTemplateDefinitionRequest,
): Promise<ReportTemplateDetailDto> => {
  const store = await ensureLocalSeed(payload.tenantId)
  const template = store.templates.find((item) => item.reportTemplateId === reportTemplateId && item.tenantId === payload.tenantId)
  if (!template) {
    throw new Error("Report template not found")
  }

  const updatedAt = nowIso()
  template.updatedAt = updatedAt

  const normalizedFields = (payload.fields ?? []).map((item, index) => ({
    ...item,
    reportTemplateFieldId: item.reportTemplateFieldId || makeId(),
    orderIndex: item.orderIndex || index + 1,
  }))

  const normalizedFilters = (payload.filters ?? []).map((item, index) => ({
    ...item,
    reportTemplateFilterId: item.reportTemplateFilterId || makeId(),
    orderIndex: item.orderIndex || index + 1,
  }))

  const detail: ReportTemplateDetailDto = {
    ...template,
    fields: normalizedFields,
    filters: normalizedFilters,
    layoutJson:
      payload.layoutJson === undefined
        ? store.details[reportTemplateId]?.layoutJson ?? null
        : normalizeLayout(payload.layoutJson) ?? null,
    updatedAt,
  }
  store.details[reportTemplateId] = detail
  writeStore(store)
  return detail
}

export async function searchReportTemplates({
  tenantId,
  search,
  status = "all",
  page = 1,
  pageSize = 20,
}: SearchReportTemplatesParams): Promise<PagedResultDto<ReportTemplateDto>> {
  if (reportApiUnavailable) {
    return searchInLocalStore({ tenantId, search, status, page, pageSize })
  }

  try {
    const response = await axios.get<PagedResultDto<ReportTemplateDto> | ReportTemplateDto[]>(
      buildUrl("/api/v1/reports/templates"),
      {
        params: {
          tenantId,
          search: search?.trim() || undefined,
          status,
          page,
          pageSize,
        },
      },
    )

    return normalizePagedResponse(response.data, page, pageSize)
  } catch (error) {
    if (!isEndpointMissing(error)) throw error
    markApiUnavailable()
    return searchInLocalStore({ tenantId, search, status, page, pageSize })
  }
}

export async function createReportTemplate(
  payload: ReportTemplateUpsertRequest,
): Promise<ReportTemplateDto> {
  if (reportApiUnavailable) {
    return createInLocalStore(payload)
  }

  try {
    const response = await axios.post<ReportTemplateDto>(
      buildUrl("/api/v1/reports/templates"),
      payload,
    )
    return response.data
  } catch (error) {
    if (!isEndpointMissing(error)) throw error
    markApiUnavailable()
    return createInLocalStore(payload)
  }
}

export async function updateReportTemplate(
  reportTemplateId: string,
  payload: ReportTemplateUpsertRequest,
): Promise<ReportTemplateDto> {
  if (reportApiUnavailable) {
    return updateInLocalStore(reportTemplateId, payload)
  }

  try {
    const response = await axios.post<ReportTemplateDto>(
      buildUrl(`/api/v1/reports/templates/${reportTemplateId}`),
      payload,
    )
    return response.data
  } catch (error) {
    if (!isEndpointMissing(error)) throw error
    markApiUnavailable()
    return updateInLocalStore(reportTemplateId, payload)
  }
}

export async function getReportTemplate(
  reportTemplateId: string,
  tenantId?: string,
): Promise<ReportTemplateDetailDto> {
  if (reportApiUnavailable) {
    return getDetailFromLocalStore(reportTemplateId, tenantId)
  }

  try {
    const response = await axios.get<any>(buildUrl(`/api/v1/reports/templates/${reportTemplateId}`), {
      params: {
        tenantId: tenantId?.trim() || undefined,
      },
    })
    return normalizeDetail(response.data)
  } catch (error) {
    if (!isEndpointMissing(error)) throw error
    markApiUnavailable()
    return getDetailFromLocalStore(reportTemplateId, tenantId)
  }
}

export async function cloneReportTemplate(
  reportTemplateId: string,
  tenantId: string,
): Promise<ReportTemplateDto> {
  if (reportApiUnavailable) {
    return cloneInLocalStore(reportTemplateId, tenantId)
  }

  try {
    const response = await axios.post<ReportTemplateDto>(
      buildUrl(`/api/v1/reports/templates/${reportTemplateId}/clone`),
      { tenantId },
    )
    return response.data
  } catch (error) {
    if (!isEndpointMissing(error)) throw error
    markApiUnavailable()
    return cloneInLocalStore(reportTemplateId, tenantId)
  }
}

export async function updateReportTemplateDefinition(
  reportTemplateId: string,
  payload: UpdateReportTemplateDefinitionRequest,
): Promise<ReportTemplateDetailDto> {
  if (reportApiUnavailable) {
    return updateDefinitionInLocalStore(reportTemplateId, payload)
  }

  try {
    const response = await axios.post<any>(
      buildUrl(`/api/v1/reports/templates/${reportTemplateId}/definition`),
      payload,
    )
    return normalizeDetail(response.data)
  } catch (error) {
    if (!isEndpointMissing(error)) {
      throw error
    }
    markApiUnavailable()
  }

  try {
    const response = await axios.post<any>(
      buildUrl(`/api/v1/reports/templates/${reportTemplateId}/config`),
      payload,
    )
    return normalizeDetail(response.data)
  } catch (error) {
    if (!isEndpointMissing(error)) {
      throw error
    }
    markApiUnavailable()
  }

  try {
    const detail = await getReportTemplate(reportTemplateId, payload.tenantId)
    const response = await axios.post<any>(
      buildUrl(`/api/v1/reports/templates/${reportTemplateId}`),
      {
        tenantId: payload.tenantId,
        code: detail.code,
        name: detail.name,
        description: detail.description,
        dataSourceType: detail.dataSourceType,
        isActive: detail.isActive,
        fields: payload.fields,
        filters: payload.filters,
        layoutJson: payload.layoutJson ?? detail.layoutJson ?? null,
      },
    )
    return normalizeDetail(response.data)
  } catch (error) {
    if (!isEndpointMissing(error)) throw error
    markApiUnavailable()
    return updateDefinitionInLocalStore(reportTemplateId, payload)
  }
}

export async function activateReportTemplate(
  reportTemplateId: string,
  tenantId: string,
  isActive: boolean,
): Promise<void> {
  if (reportApiUnavailable) {
    await activateInLocalStore(reportTemplateId, tenantId, isActive)
    return
  }

  try {
    await axios.post(buildUrl(`/api/v1/reports/templates/${reportTemplateId}/activate`), {
      tenantId,
      isActive,
    })
  } catch (error) {
    if (!isEndpointMissing(error)) throw error
    markApiUnavailable()
    await activateInLocalStore(reportTemplateId, tenantId, isActive)
  }
}

export async function runReport(
  payload: RunReportRequest,
): Promise<RunReportResult> {
  if (reportApiUnavailable) {
    return runFromPermitFallback(payload)
  }

  try {
    const response = await axios.post<RunReportResult>(buildUrl("/api/v1/reports/run"), payload)
    return {
      ...response.data,
      source: "api",
    }
  } catch (error) {
    if (!isEndpointMissing(error)) throw error
    markApiUnavailable()
    return runFromPermitFallback(payload)
  }
}

export async function exportReportPdf(payload: ExportReportPdfRequest): Promise<Blob | null> {
  if (reportApiUnavailable) {
    return null
  }

  try {
    const response = await axios.post(
      buildUrl("/api/v1/reports/export/pdf"),
      payload,
      { responseType: "blob" },
    )
    return response.data as Blob
  } catch (error) {
    if (!isEndpointMissing(error)) throw error
    markApiUnavailable()
    return null
  }
}

export async function uploadReportTemplateImage(
  payload: UploadReportTemplateImageRequest,
): Promise<UploadReportTemplateImageResponse> {
  const tenantId = payload.tenantId.trim()
  if (!tenantId) {
    throw new Error("tenantId is required")
  }

  const formData = new FormData()
  formData.append("tenantId", tenantId)
  formData.append(
    "permitNumber",
    payload.permitNumber?.trim() || REPORT_IMAGE_UPLOAD_PERMIT_NUMBER,
  )

  const documentCode = payload.documentCode?.trim() || REPORT_IMAGE_UPLOAD_DOCUMENT_CODE
  if (documentCode) {
    formData.append("documentCode", documentCode)
  }
  formData.append("file", payload.file)

  const response = await axios.post<{
    fileName: string
    storedFileName: string
    storagePath: string
    contentType: string
    size: number
  }>(
    buildUrl("/api/v1/attachments/upload"),
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  )

  const raw = response.data
  const storagePath = String(raw?.storagePath || "").trim()
  return {
    fileName: String(raw?.fileName || ""),
    storedFileName: String(raw?.storedFileName || ""),
    storagePath,
    contentType: String(raw?.contentType || ""),
    size: Number(raw?.size || 0),
    fileUrl: buildAttachmentFileUrl(storagePath),
  }
}

export async function listReportDatasourceViews(
  tenantId: string,
  search?: string,
): Promise<ReportDatasourceViewOptionDto[]> {
  const response = await axios.get<ReportDatasourceViewOptionDto[]>(
    buildUrl("/api/v1/reports/datasources/views"),
    {
      params: {
        tenantId: tenantId.trim(),
        search: search?.trim() || undefined,
      },
    },
  )

  return Array.isArray(response.data)
    ? response.data.map((item) => ({
        schemaName: String(item?.schemaName || "").trim(),
        viewName: String(item?.viewName || "").trim(),
        objectName: String(item?.objectName || "").trim(),
      }))
    : []
}

export async function previewReportDatasource(
  payload: PreviewReportDatasourceRequest,
): Promise<PreviewReportDatasourceResult> {
  const response = await axios.post<PreviewReportDatasourceResult>(
    buildUrl("/api/v1/reports/datasources/preview"),
    payload,
  )

  return {
    cardinality: response.data?.cardinality === "table" ? "table" : "record",
    rowCount: Number(response.data?.rowCount || 0),
    columns: Array.isArray(response.data?.columns)
      ? response.data.columns.map(normalizeDatasourceField).filter((item) => item.fieldKey)
      : [],
    record:
      response.data?.record && typeof response.data.record === "object"
        ? response.data.record
        : null,
    rows: Array.isArray(response.data?.rows) ? response.data.rows : [],
  }
}
